<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const data = ref(null);
const loading = ref(false);
const errorMsg = ref('');
const active = ref('all'); // tarih ön ayarı

// Tarih ön ayarları → {start, end} (ISO, yerel). 'all' = tüm 2026 (filtresiz).
const PRESETS = [
  { key: 'all', label: 'Tüm 2026' },
  { key: 'this_month', label: 'Bu Ay' },
  { key: 'last_month', label: 'Geçen Ay' },
  { key: 'last_3', label: 'Son 3 Ay' },
];

const iso = d => d.toISOString().slice(0, 10);
const rangeFor = key => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  if (key === 'this_month')
    return { start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)) };
  if (key === 'last_month')
    return { start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)) };
  if (key === 'last_3')
    return {
      start: iso(new Date(y, m - 2, 1)),
      end: iso(new Date(y, m + 1, 0)),
    };
  return {};
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const { start, end } = rangeFor(active.value);
    const qs = new URLSearchParams();
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    const path = `consolidated_pnl/overview${qs.toString() ? `?${qs}` : ''}`;
    data.value = await fetchMarketplaceJson(accountId, path);
  } catch (e) {
    errorMsg.value = e.message || 'Veri alınamadı.';
  } finally {
    loading.value = false;
  }
};

const setPreset = key => {
  active.value = key;
  load();
};

onMounted(load);

const tl = n =>
  `${Number(n || 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ₺`;

const total = computed(() => data.value?.total);
const companies = computed(() => data.value?.companies || []);
const isLoss = computed(() => (total.value?.net_pnl ?? 0) < 0);

// AI anlatısı — sade Türkçe, durumu tek paragrafta özetler.
const narrative = computed(() => {
  if (!total.value) return '';
  const t = total.value;
  const durum = t.net_pnl < 0 ? 'ZARARDA' : 'KÂRDA';
  const parts = [
    `Seçili dönemde işletme genel olarak ${durum}: ${tl(t.net_pnl)}.`,
    `Mağazaların operasyonel kârı ${tl(t.operational_net)}, ama genel giderler (maaş, kira, SGK, vergi, kredi, fatura) toplam ${tl(t.overhead)} bunu aşağı çekiyor.`,
  ];
  companies.value.forEach(c => {
    const d = c.net_pnl < 0 ? 'zarar' : 'kâr';
    parts.push(
      `${c.label}: operasyon ${tl(c.operational_net)}, overhead ${tl(c.overhead)} → ${d} ${tl(c.net_pnl)}.`
    );
  });
  if (t.personal_overhead > 0) {
    parts.push(
      `Ayrıca şahsi hesabından ödenen giderler (ör. personel maaşı) ${tl(t.personal_overhead)} genel toplamdan düşüldü.`
    );
  }
  return parts.join(' ');
});
</script>

<template>
  <div class="p-6 max-w-5xl mx-auto">
    <div class="flex items-center justify-between flex-wrap gap-3 mb-4">
      <h1 class="text-3xl font-extrabold text-n-slate-12">
        Gerçek Kâr / Zarar
      </h1>
      <div class="flex gap-1.5 flex-wrap">
        <button
          v-for="p in PRESETS"
          :key="p.key"
          class="px-4 py-2 text-base font-semibold rounded-lg border transition"
          :class="
            active === p.key
              ? 'bg-n-brand text-white border-n-brand'
              : 'bg-n-slate-2 text-n-slate-11 border-n-slate-5 hover:bg-n-slate-3'
          "
          @click="setPreset(p.key)"
        >
          {{ p.label }}
        </button>
      </div>
    </div>

    <p v-if="loading" class="text-lg text-n-slate-11 py-10 text-center">
      Hesaplanıyor…
    </p>
    <p
      v-else-if="errorMsg"
      class="text-lg text-ruby-700 py-10 text-center font-semibold"
    >
      {{ errorMsg }}
    </p>

    <template v-else-if="total">
      <!-- HERO: toplam gerçek kâr/zarar -->
      <div
        class="rounded-2xl p-6 mb-5 border-2"
        :class="
          isLoss
            ? 'bg-ruby-50 border-ruby-300 dark:bg-ruby-900/20 dark:border-ruby-700'
            : 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700'
        "
      >
        <div class="text-lg font-semibold text-n-slate-11 mb-1">
          Toplam Gerçek Kâr / Zarar (2 şirket)
        </div>
        <div
          class="text-5xl font-black tabular-nums"
          :class="
            isLoss
              ? 'text-ruby-700 dark:text-ruby-400'
              : 'text-green-700 dark:text-green-400'
          "
        >
          {{ tl(total.net_pnl) }}
        </div>
        <p class="mt-3 text-lg leading-relaxed text-n-slate-12">
          {{ narrative }}
        </p>
      </div>

      <!-- Şirket bazlı şelale kartları -->
      <div class="grid md:grid-cols-2 gap-5">
        <div
          v-for="c in companies"
          :key="c.key"
          class="rounded-2xl border border-n-slate-5 bg-n-solid-1 p-5"
        >
          <h2 class="text-xl font-bold text-n-slate-12 mb-4">{{ c.label }}</h2>

          <div class="space-y-2 text-base">
            <div
              v-for="ch in c.channels"
              :key="ch.key"
              class="flex justify-between items-center"
            >
              <span class="text-n-slate-11">{{ ch.label }} net</span>
              <span
                class="font-semibold tabular-nums"
                :class="ch.net < 0 ? 'text-ruby-600' : 'text-n-slate-12'"
                >{{ tl(ch.net) }}</span>
            </div>

            <div
              class="flex justify-between items-center pt-2 border-t border-n-slate-4 font-bold"
            >
              <span class="text-n-slate-12">Operasyonel Kâr</span>
              <span
                class="tabular-nums"
                :class="
                  c.operational_net < 0 ? 'text-ruby-600' : 'text-green-700'
                "
                >{{ tl(c.operational_net) }}</span>
            </div>

            <div class="flex justify-between items-center text-ruby-600">
              <span>− Banka K/Z gideri</span>
              <span class="font-semibold tabular-nums">{{
                tl(c.bank_overhead)
              }}</span>
            </div>
            <div class="flex justify-between items-center text-ruby-600">
              <span>− Fatura K/Z gideri</span>
              <span class="font-semibold tabular-nums">{{
                tl(c.invoice_overhead)
              }}</span>
            </div>

            <div
              class="flex justify-between items-center pt-3 mt-1 border-t-2 border-n-slate-6"
            >
              <span class="text-lg font-extrabold text-n-slate-12">
                = Gerçek Kâr / Zarar
              </span>
              <span
                class="text-2xl font-black tabular-nums"
                :class="c.net_pnl < 0 ? 'text-ruby-700' : 'text-green-700'"
                >{{ tl(c.net_pnl) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Şahsi hesaptan ödenen K/Z (nötr — şirkete yazılmadı, toplamdan düşülür) -->
      <div
        v-if="total.personal_overhead > 0"
        class="mt-5 rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-5"
      >
        <div class="flex justify-between items-center">
          <span class="text-base font-semibold text-n-slate-12">
            Şahsi hesaptan ödenen giderler (ör. personel maaşı)
          </span>
          <span class="text-xl font-black tabular-nums text-ruby-700">
            − {{ tl(total.personal_overhead) }}
          </span>
        </div>
        <p class="mt-1 text-sm text-n-slate-10">
          Henüz bir şirkete yazılmadı; genel toplamdan düşülüyor.
        </p>
      </div>

      <p class="mt-5 text-sm text-n-slate-10 leading-relaxed">
        Not: Her kanalın net'i kendi panelinden gelir (komisyon, kargo, reklam,
        ürün maliyeti zaten içinde). Buradan yalnızca <b>Banka Defteri</b> ve
        <b>Fatura</b> sayfalarında "Kâr-Zarara Esas Gider" olarak işaretlediğin
        genel giderler düşülür. Operasyonel bir gideri (kargo/komisyon/reklam) o
        havuzlara koyarsan çift sayılır — onları işaretleme.
      </p>
    </template>
  </div>
</template>
